import React, { useState, useCallback } from 'react';
import {
  CustomCollapseCard,
  CustomTable,
  Text,
  CustomButton,
  CustomImage,
  confirmDialog,
  successDialog,
  useText,
} from '@src/components';
import '@src/styles/managementUser/userList.scss';
import dayjs from '@src/utils/day';
import { dateFormat2 } from '@src/utils/keys';
import iconPlus from '@src/assets/icons/icon-plus.svg';
import { useHistory } from 'react-router-dom';
import { DownCircleOutlined, UpCircleOutlined } from '@ant-design/icons';
import NoteModal from './NoteModal';
import warning from '@src/assets/icons/icon-warning.svg';
import { Alert } from 'antd';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { stringCut } from '@src/utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import {
  custUserStatusChange,
  custUserDelete,
  userByFilterList,
  clearTable,
} from '@src/store/slice/custUsersSlice';
import { updateDetail, resetUserInfo } from '@src/store/slice/userSlice';

function getExpandIcon() {
  return ({ expanded, onExpand, record }) =>
    expanded ? (
      <UpCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    ) : (
      <DownCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    );
}

function getExpandedRowRender(handleNavigateEditPage, handleChangeStatus, handleDelete) {
  return (record) => (
    <div className="custom-expanded-row" key={record.userId}>
      <div className="colum-title">
        <span>
          <Text t="productName" />
        </span>{' '}
        <span>{record.categories}</span>
      </div>
      <div className="colum-title">
        <span>
          <Text t="eMailAddress2" />
        </span>{' '}
        <span>{record.email}</span>
      </div>
      <div className="colum-title">
        <span>
          <Text t="status2" />
        </span>{' '}
        <span>{record.status}</span>
      </div>
      <div className="colum-title">
        <span>
          <Text t="userName2" />
        </span>{' '}
        <span>{record.userName}</span>
      </div>
      <div className="colum-title">
        <span>
          <Text t="accountCreationDate" />
        </span>
        <span>{dayjs(record.insertTime).format(dateFormat2)}</span>
      </div>
      <div className="colum-title">
        <span>
          <Text t="lastUpdateDate" />
        </span>
        <span>{dayjs(record.updateTime).format(dateFormat2)}</span>
      </div>
      <div className="colum-title">
        <span>
          <Text t="lastUpdatedBy" />
        </span>{' '}
        <span>{record.updaterFullName || '-'}</span>
      </div>

      <div className="mobile-action-btn">
        {!record?.isDisabled && (
          <CustomButton
            id="companyInformation-managementUser-userList-edit-btn"
            buttonAuthName="UserManagementEdit"
            className="edit-btn"
            onClick={() => handleNavigateEditPage(record.userId)}
          >
            <Text t="edit" />
          </CustomButton>
        )}

        {!record?.isDisabled &&
          (record.status ? (
            <CustomButton
              id="companyInformation-managementUser-userList-passive-btn"
              buttonAuthName="UserManagementActivePassive"
              className="passive-btn"
              onClick={() => handleChangeStatus(record.userId, false)}
            >
              <Text t="deactivate" />
            </CustomButton>
          ) : (
            <CustomButton
              id="companyInformation-managementUser-userList-active-btn"
              buttonAuthName="UserManagementActivePassive"
              className="active-btn"
              onClick={() => handleChangeStatus(record.userId, true)}
            >
              <Text t="activate" />
            </CustomButton>
          ))}
        {!record?.isDisabled && (
          <CustomButton
            id="companyInformation-managementUser-userList-delete-btn"
            buttonAuthName="UserManagementDelete"
            className="delete-btn"
            onClick={() => handleDelete(record.userId)}
          >
            <Text t="delete" />
          </CustomButton>
        )}
      </div>
    </div>
  );
}

const UserList = ({ form }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [modalVisible, setModalVisible] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const isMobil = useMediaQuery({ query: '(max-width: 991.98px)' });
  const { selectedCustomer } = useSelector((state) => state?.customer);

  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  
  const {
    table: { detail, tableProperty },
    filterObject,
  } = useSelector((state) => state?.custUsers);
  const expandRow = useCallback((rowKey) => {
    setExpandedRows((expandedRows) => {
      if (expandedRows.includes(rowKey)) {
        return expandedRows.filter((id) => id !== rowKey);
      }
      return [...expandedRows, rowKey];
    });
  }, []);

  const expandButton = (record) => {
    let buttonText = <Text t="detailButton" />;
    if (expandedRows !== undefined && expandedRows !== null) {
      if (expandedRows.includes(record.userId)) {
        buttonText = <Text t="detailButtonHide" />;
      }
    } else {
      setExpandedRows([]);
    }
    return (
      <CustomButton
        id="companyInformation-managementUser-userList-detail-btn"
        className="detail-btn"
        onClick={() => expandRow(record.userId)}
      >
        {buttonText}
      </CustomButton>
    );
  };

  const handleModal = () => {
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    confirmDialog({
      title: <Text t="attention" />,
      htmlContent: <Text t="areYouSureYouWantToDeleteThe2" />,
      onOk: async () => {
        const action = await dispatch(custUserDelete(id));
        if (custUserDelete.fulfilled.match(action)) {
          successDialog({
            title: <Text t="successfullySent" />,
            message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
            onOk: async () => {
              dispatch(clearTable());
              dispatch(
                userByFilterList({
                  body:
                    Object.keys(filterObject).length === 0
                      ? { customerId: selectedCustomer?.customerId }
                      : filterObject,
                  pageNumber: tableProperty?.currentPage,
                  pageSize: tableProperty?.pageSize,
                }),
              );
            },
          });
        }
      },
    });
  };

  const handleChangeStatus = async (id, status) => {
    const action = await dispatch(custUserStatusChange({ body: { id, status } }));

    if (custUserStatusChange.fulfilled.match(action)) {
      successDialog({
        title: <Text t="successfullySent" />,
        message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
        onOk: async () => {
          dispatch(clearTable());
          dispatch(
            userByFilterList({
              body:
                Object.keys(filterObject).length === 0
                  ? { customerId: selectedCustomer?.customerId }
                  : filterObject,
              pageNumber: tableProperty?.currentPage,
              pageSize: tableProperty?.pageSize,
            }),
          );
        },
      });
    }
  };

  const columns = [
    {
      title: <Text t="nameSurnameUpperCase" />,
      dataIndex: 'name',
      key: 'name',
      width: 130,
      ellipsis: true,
      render: (_, record) => {
        return `${record.name} ${record.surName}`;
      },
    },
    {
      title: <Text t="roleNameUpperCase" />,
      dataIndex: 'groupName',
      key: 'groupName',
      width: 90,
      align: 'left',
      ellipsis: true,
    },
    {
      title: <Text t="productNameUpperCase" />,
      dataIndex: 'categories',
      key: 'categories',
      width: 70,
      align: 'center',
      responsive: ['md'],
      ellipsis: true,
    },
    {
      title: <Text t="emailUpperCase" />,
      dataIndex: 'email',
      key: 'email',
      width: 200,
      align: 'left',
      responsive: ['md'],
      ellipsis: true,
    },
    {
      title: <Text t="status4" />,
      dataIndex: 'status',
      key: 'status',
      width: 70,
      align: 'left',
      responsive: ['md'],
      ellipsis: true,
      render: (val, record) => {
        return val ? 'Aktif' : 'Pasif';
      },
    },
    {
      title: '',
      width: 200,
      align: 'center',
      ellipsis: true,
      dataIndex: '',
      key: 'x',
      responsive: ['xl'],
      render: (text, record) => {
        return (
          <div className="table-action-content">
            {expandButton(record)}

            {!record?.isDisabled && (
              <CustomButton
                id="companyInformation-managementUser-userList-second-edit-btn"
                className="edit-btn"
                onClick={() => handleNavigateEditPage(record.userId)}
                buttonAuthName="UserManagementEdit"
              >
                <Text t="edit" />
              </CustomButton>
            )}

            {!record.isDisabled &&
              (record.status ? (
                <CustomButton
                  id="companyInformation-managementUser-userList-second-passive-btn"
                  buttonAuthName="UserManagementActivePassive"
                  className="passive-btn"
                  onClick={() => handleChangeStatus(record.userId, false)}
                >
                  <Text t="deactivate" />
                </CustomButton>
              ) : (
                <CustomButton
                  id="companyInformation-managementUser-userList-second-active-btn"
                  buttonAuthName="UserManagementActivePassive"
                  className="active-btn"
                  onClick={() => handleChangeStatus(record.userId, true)}
                >
                  <Text t="activate" />
                </CustomButton>
              ))}

            {!record?.isDisabled && (
              <CustomButton
                id="companyInformation-managementUser-userList-second-delete-btn"
                buttonAuthName="UserManagementDelete"
                className="delete-btn"
                onClick={() => handleDelete(record.userId)}
              >
                <Text t="delete" />
              </CustomButton>
            )}
          </div>
        );
      },
    },
  ];

  const handlePaginationChange = useCallback(
    async (pageNumber, pageSize) => {
      dispatch(clearTable());
      await dispatch(
        userByFilterList({
          body:
            Object.keys(filterObject).length === 0
              ? { customerId: selectedCustomer?.customerId }
              : filterObject,
          pageNumber,
          pageSize,
        }),
      );
    },
    [dispatch, filterObject],
  );

  const handleNavigateEditPage = (userId) => {
    dispatch(resetUserInfo());
    dispatch(updateDetail({ userId, customerId: detail?.customerId }));
    history.push('/company-information/management-user/edit-user');
  };

  const handleNavigateAddPage = () => {
    dispatch(updateDetail({ customerName: detail?.customerName, customerId: detail?.customerId }));
    history.push('/company-information/management-user/new-user');
  };

  return (
    <CustomCollapseCard className="user-list-card" cardTitle={<Text t="userList" />}>
      <div className="table-header">
        <div className="info-list">
          <div className="list-item">
            <p>
              <Text t="customerName2" /> <span>{detail?.customerName || '-'}</span>
            </p>
            <p>
              <Text t="customerNumber" />
              <span>{detail?.customerId || '-'}</span>
            </p>
          </div>
          <p>
            Not:
            {!isMobil ? (
              <>
                <span>{stringCut(49, detail?.note || '-')}</span>{' '}
                {detail?.note?.length > 49 && (
                  <span className="detail-btn" onClick={() => handleModal()}>
                    <Text t="continued" />
                  </span>
                )}
              </>
            ) : (
              <>
                <span>{detail?.note || '-'}</span>
              </>
            )}
          </p>
        </div>
        <div className="action-btn-content">
          <CustomButton
            id="companyInformation-managementUser-userList-add-btn"
            buttonAuthName="UserManagementAdd"
            className="add-btn"
            onClick={handleNavigateAddPage}
          >
            <CustomImage className="icon-add" src={iconPlus} />
            <Text t="addNewUser" />
          </CustomButton>
        </div>
      </div>
      {detail?.customerUsers?.items?.length > 0 ? (
        <>
          <CustomTable
            pagination={{
              total: tableProperty?.totalItems,
              pageSize: tableProperty?.pageSize,
              current: tableProperty?.currentPage,
              defaultCurrent: 1,
              onChange: (pageNumber, pageSize) => handlePaginationChange(pageNumber, pageSize),
            }}
            isScroll={false}
            expandedRowKeys={expandedRows}
            expandable={{
              showExpandColumn: false,
              expandIconColumnIndex: 7,
              onExpand: (expanded, record, key) =>
                expandedRows === key ? setExpandedRows(null) : setExpandedRows(key),
              expandIcon: getExpandIcon(),
              expandedRowRender: getExpandedRowRender(
                handleNavigateEditPage,
                handleChangeStatus,
                handleDelete,
              ),
            }}
            dataSource={detail?.customerUsers?.items || []}
            columns={columns}
            className="user-list-table"
            rowKey={(record) => record?.userId}
          />
        </>
      ) : (
        <Alert
          message={
            <div className="alert-message-contain">
              <CustomImage src={warning} />
              <span>
                {' '}
                <Text t="belongingToTheCustomerYouSelected" />{' '}
                <b>
                  <Text t="user" />
                </b>{' '}
                <Text t="isNotAvailable" />{' '}
              </span>
            </div>
          }
          banner
          showIcon={false}
          className="custom-alert-warning"
        />
      )}

      <NoteModal
        modalVisible={modalVisible}
        handleModalVisible={setModalVisible}
        text={detail?.note}
      />
    </CustomCollapseCard>
  );
};

export default UserList;
